import React, { useState, useEffect } from 'react';
import { X, Check, Info } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { cookieCategories } from '../../constants/cookieCategories';
import { useCookieConsent } from '../../contexts/CookieConsentContext';
import { CookiePreferences } from '../../types/cookies';

interface CookiePreferencesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const CookiePreferencesModal: React.FC<CookiePreferencesModalProps> = ({ isOpen, onClose }) => {
  const { consentStatus, updateConsent } = useCookieConsent();
  const [preferences, setPreferences] = useState<CookiePreferences>({});

  useEffect(() => {
    if (consentStatus) {
      setPreferences(consentStatus.preferences);
    } else {
      const defaultPreferences = cookieCategories.reduce((acc, category) => ({
        ...acc,
        [category.id]: category.required || false
      }), {});
      setPreferences(defaultPreferences);
    }
  }, [consentStatus]);

  const handleToggle = (categoryId: string) => {
    if (cookieCategories.find(c => c.id === categoryId)?.required) return;
    setPreferences(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const handleSave = () => {
    updateConsent(preferences);
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="cookie-preferences-title" role="dialog" aria-modal="true">
          <div className="flex min-h-screen items-center justify-center p-4 text-center sm:p-0">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/30 backdrop-blur-sm transition-opacity z-0" 
              aria-hidden="true" 
              onClick={onClose}
            />

            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{
                type: "spring",
                stiffness: 350,
                damping: 25,
                mass: 1
              }}
              className="relative inline-block w-full transform overflow-hidden rounded-2xl bg-white text-left shadow-xl transition-all sm:my-8 sm:max-w-2xl z-10"
            >
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="w-full">
                    <motion.div 
                      initial={{ x: -20, opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      transition={{ delay: 0.1 }}
                      className="flex items-center justify-between mb-6"
                    >
                      <h3 className="text-2xl font-bold text-gray-900" id="cookie-preferences-title">
                        Cookie-Einstellungen
                      </h3>
                      <button
                        onClick={onClose}
                        className="rounded-lg p-2 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-success transition-colors duration-200"
                      >
                        <X className="h-6 w-6" />
                      </button>
                    </motion.div>

                    <div className="space-y-6">
                      {cookieCategories.map((category, index) => (
                        <motion.div
                          key={category.id}
                          initial={{ x: -20, opacity: 0 }}
                          animate={{ x: 0, opacity: 1 }}
                          transition={{ delay: 0.1 + index * 0.1 }}
                          className="rounded-xl border border-gray-200 p-4 hover:border-success transition-colors duration-200"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center space-x-2">
                                <h4 className="text-lg font-semibold text-gray-900">{category.name}</h4>
                                {category.required && (
                                  <span className="inline-flex items-center rounded-full bg-success/10 px-2.5 py-0.5 text-xs font-medium text-success">
                                    Erforderlich
                                  </span>
                                )}
                              </div>
                              <p className="mt-1 text-sm text-gray-500">{category.description}</p>
                            </div>
                            <div className="ml-4 flex-shrink-0">
                              <button
                                type="button"
                                onClick={() => handleToggle(category.id)}
                                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-success focus:ring-offset-2 ${
                                  preferences[category.id] ? 'bg-success' : 'bg-gray-200'
                                } ${category.required ? 'cursor-not-allowed opacity-50' : ''}`}
                                disabled={category.required}
                                role="switch"
                                aria-checked={preferences[category.id]}
                              >
                                <motion.span
                                  layout
                                  className="pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0"
                                  animate={{
                                    x: preferences[category.id] ? 20 : 0,
                                  }}
                                  transition={{ type: "spring", stiffness: 500, damping: 30 }}
                                />
                              </button>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>

                    <motion.div
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.4 }}
                      className="mt-8 text-sm text-gray-500"
                    >
                      <div className="flex items-start space-x-2">
                        <Info className="h-5 w-5 flex-shrink-0 text-gray-400" />
                        <p>
                          Wir verwenden Cookies, um Ihnen die bestmögliche Erfahrung auf unserer Website zu bieten. 
                          Weitere Informationen finden Sie in unserer{' '}
                          <a href="/datenschutz" className="text-success hover:text-dark transition-colors duration-200">
                            Datenschutzerklärung
                          </a>.
                        </p>
                      </div>
                    </motion.div>
                  </div>
                </div>
              </div>

              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6"
              >
                <button
                  type="button"
                  onClick={handleSave}
                  className="inline-flex w-full justify-center rounded-xl bg-success px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-dark focus:outline-none focus:ring-2 focus:ring-success sm:ml-3 sm:w-auto transition-colors duration-200"
                >
                  Einstellungen speichern
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="mt-3 inline-flex w-full justify-center rounded-xl bg-white px-4 py-2.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto transition-colors duration-200"
                >
                  Abbrechen
                </button>
              </motion.div>
            </motion.div>
          </div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default CookiePreferencesModal;